import * as THREE from 'three';
import type { Vec3 } from '../../sim/mathx';
import type { InterpolatedTelemetry } from '../../state/interpolate';
import { setThreePositionFromEnu } from '../entities/ScoutView';

type InterpolatedTrack = InterpolatedTelemetry['tracks'][number];

/** Selected-track ring and persistent scout-to-track link geometry. */
export class SelectedTrackHalo extends THREE.Group {
  readonly halo: THREE.Mesh<THREE.TorusGeometry, THREE.MeshBasicMaterial>;
  readonly link: THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial>;

  private disposed = false;

  constructor() {
    super();
    this.name = 'radar-viz:halo';
    this.halo = new THREE.Mesh(
      new THREE.TorusGeometry(1.25, 0.09, 8, 40),
      new THREE.MeshBasicMaterial({ color: 0xffff70, transparent: true, opacity: 0.9 }),
    );
    this.halo.name = 'radar-viz:selected-ring';

    const linkGeometry = new THREE.BufferGeometry();
    const linkPositions = new THREE.BufferAttribute(new Float32Array(6), 3);
    linkPositions.setUsage(THREE.DynamicDrawUsage);
    linkGeometry.setAttribute('position', linkPositions);
    this.link = new THREE.Line(
      linkGeometry,
      new THREE.LineBasicMaterial({ color: 0xffee66, transparent: true, opacity: 0.55 }),
    );
    this.link.name = 'radar-viz:scout-track-link';
    this.link.frustumCulled = false;
    this.add(this.halo, this.link);
    this.hideSelection();
  }

  update(
    selectedTrackId: number | null,
    tracks: readonly InterpolatedTrack[],
    scoutEnu: Vec3,
  ): void {
    const selected =
      selectedTrackId === null ? undefined : tracks.find((track) => track.id === selectedTrackId);
    if (!selected) {
      this.hideSelection();
      return;
    }

    setThreePositionFromEnu(this.halo, selected.posEnu);
    const position = this.link.geometry.getAttribute('position') as THREE.BufferAttribute;
    position.setXYZ(0, scoutEnu.x, scoutEnu.y, scoutEnu.z);
    position.setXYZ(1, selected.posEnu.x, selected.posEnu.y, selected.posEnu.z);
    position.needsUpdate = true;
    this.link.geometry.computeBoundingSphere();
    this.halo.visible = true;
    this.link.visible = true;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.removeFromParent();
    this.halo.geometry.dispose();
    this.halo.material.dispose();
    this.link.geometry.dispose();
    this.link.material.dispose();
    this.clear();
  }

  private hideSelection(): void {
    this.halo.visible = false;
    this.link.visible = false;
  }
}
